import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { STEP_GATE_NAMES, STEP_TIPS } from 'accounts/constants/steps';
import { useProperties } from 'accounts/hooks/homeowners/use-properties';
import { useSteps } from 'accounts/hooks/homeowners/use-steps';
import {
  getFilteredGateSteps,
  getStepPath,
  isCurrentPropertyOrUnitStep,
  isStepCompleted,
  isStepUnHidden,
} from 'accounts/utils/flow-steps';
import { getSetupFlowSteps } from 'accounts/utils/steps';
import { findIndex } from 'es-toolkit/compat';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { FlowType } from 'models/enums';
import { BASE_PATHS } from 'routes/paths';
import { trackMixpanelEvent } from '@belong/common';
import {
  fetchStepData as fetchHomeInspectionStepData,
  updateStepData as updateHomeInspectionStepData,
} from 'store/redux/home-inspection-flow';
import {
  fetchStepData as fetchHomeListingStepData,
  updateStepData as updateHomeListingStepData,
} from 'store/redux/home-listing-flow';
import {
  fetchStepData as fetchHomeownerSetUpStepData,
  updateStepData as updateHomeownerSetUpStepData,
} from 'store/redux/homeowner-set-up-flow/actions';
import { showToast } from 'store/redux/toast';
import {
  fetchStepData as fetchUserPaymentStepData,
  updateStepData as updateUserPaymentStepData,
} from 'store/redux/user-payment-flow';
import { selectUser } from 'store/redux/user/selectors';
import { formatString } from 'strings';
import { useFlows } from './use-flows';
import { useSelectedProperty } from './use-selected-property';
import { useSelectedUnit } from './use-selected-unit';

export function useSetupFlowSteps() {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    propertyId,
    unitId,
    step: stepParam,
    stepId,
    flowName,
  } = useParams<any>();
  const user = useSelector(selectUser);
  const { flows, fetchFlows } = useFlows();
  const { isEnabled: isAccountRedesignEnabled } =
    useFeatureFlag('account-redesign');

  const { properties, fetchProperties } = useProperties();
  const { selectedProperty } = useSelectedProperty();
  const { selectedUnit } = useSelectedUnit();
  const [goNext, setGoNext] = useState(false);

  const setupFlows = flows?.filter((flow: any) => {
    if (
      flow.flowType === FlowType.HomeownerSetup ||
      flow.flowType === FlowType.HomeInspection ||
      flow.flowType === FlowType.HomeListing
    ) {
      const isCurrentPropertyOrUnit = flow.steps?.some((step: any) =>
        isCurrentPropertyOrUnitStep(step, selectedProperty),
      );
      return isCurrentPropertyOrUnit;
    }

    if (flow.flowType === FlowType.UserPayment) {
      return flow.steps?.some(
        (step: any) => step.dataUniqueId === user?.userId,
      );
    }

    return false;
  });

  const propertyWithAdoptedResidents = selectedProperty?.units?.some(
    (unit: any) => unit.isAdoptedAgreement,
  );

  const { steps, allStepsByGate } = useSteps({ properties, setupFlows });
  const [loading, setLoading] = useState(true);
  const [stepsConfig, setStepsConfig] =
    useState<ReturnType<typeof getSetupFlowSteps>>();
  /**
   * Inferred from the initial value alone, this state's type is `null` for both
   * fields — which cannot even describe its own happy path, since `fetchData`
   * below assigns a step and its form data. `useSteps` builds each step by
   * spreading a generated `FlowStepModel` over a `STEPS_CONFIGURATION` entry and
   * adding flow/user/property fields, and the result is handed to ~20 step forms
   * through `SetupFlowStepProvider`, whose context already declares
   * `currentStep: any` for that reason. This restores that declared contract
   * rather than inventing a shape for it here.
   */
  const [currentStepState, setCurrentStepState] = useState<{
    currentStep: any;
    currentStepFormData: any;
  }>({
    currentStep: null,
    currentStepFormData: null,
  });

  /**
   * NOTE: This useEffect hook controls navigation between steps.
   * It sets a flag (goNext) to delay navigation until handleSaveAndNext() completes.
   * This ensures the correct data is used for navigation,
   * solving an issue where the last data wasn't being captured correctly.
   */
  useEffect(() => {
    if (goNext) {
      goNextStep();
      setGoNext(false);
    }
  }, [goNext]);

  // `getFilteredGateSteps` accumulates into an untyped `{}`, so its return type
  // describes nothing. It is a gate-name -> steps map, which is how every read
  // below and every consumer of `stepsByGate` already treats it.
  const stepsByGate: Record<string, any[]> = allStepsByGate
    ? getFilteredGateSteps({
        allStepsByGate,
        unitId,
        selectedProperty,
        user,
      })
    : {};

  const { currentStep, currentStepFormData } = currentStepState;

  const fetchData = useCallback(
    async () => {
      const step = steps?.find((actualStep) => actualStep.uniqueId === stepId);
      if (!step) {
        setCurrentStepState({
          currentStep: null,
          currentStepFormData: null,
        });

        return null;
      }

      try {
        let currentStepFormDataResponse;

        if (step.flowType === FlowType.HomeownerSetup) {
          currentStepFormDataResponse = await dispatch(
            fetchHomeownerSetUpStepData(step.flowId, step),
          );
        }
        if (step.flowType === FlowType.UserPayment) {
          currentStepFormDataResponse = await dispatch(
            fetchUserPaymentStepData(step.flowId, step),
          );
        }
        if (step.flowType === FlowType.HomeInspection) {
          currentStepFormDataResponse = await dispatch(
            fetchHomeInspectionStepData(step.flowId, step),
          );
        }
        if (step.flowType === FlowType.HomeListing) {
          currentStepFormDataResponse = await dispatch(
            fetchHomeListingStepData(step.flowId, step),
          );
        }
        const newCurrentStepFormData = currentStepFormDataResponse || {};

        setCurrentStepState({
          currentStep: step,
          currentStepFormData: newCurrentStepFormData,
        });

        return newCurrentStepFormData;
      } catch (error) {
        setCurrentStepState({
          currentStep: step,
          currentStepFormData: {},
        });

        return {};
      }
    },
    // steps never change but when it goes from not having length to having length, so this should be safe

    [dispatch, stepId, steps?.length],
  );

  function isLastStep() {
    const filteredSteps =
      stepsByGate?.[currentStep?.gate]?.filter(isStepUnHidden);

    // With no steps for this gate, `findIndex` below gives -1 and `undefined - 1`
    // is NaN, so the comparison was already false either way.
    if (!filteredSteps) {
      return false;
    }

    const currentStepIndex = findIndex(
      filteredSteps,
      (step: any) => step.uniqueId === currentStep?.uniqueId,
    );

    return currentStepIndex === filteredSteps.length - 1;
  }

  async function goNextStep() {
    let filteredSteps =
      stepsByGate?.[currentStep?.gate]?.filter(isStepUnHidden);

    const currentStepIndex = findIndex(
      filteredSteps,
      (step: any) => step.uniqueId === currentStep.uniqueId,
    );
    const nextStepIndex = currentStepIndex + 1;
    const nextStep = filteredSteps?.[nextStepIndex];

    if (nextStep) {
      history.push(getStepPath(nextStep, propertyId, unitId));
    } else {
      if (isAccountRedesignEnabled) {
        history.push('/redirect/flows/homeowner/post-auth');

        return;
      }

      if (currentStep.gate === STEP_GATE_NAMES.GetPaid) {
        history.push(`/accounts/portfolio/properties/${propertyId}`);
      }

      const { units } = selectedProperty || {};

      const currentUnitIndex = findIndex(
        units,
        (unit: any) => unit.basicInfo.unitId === unitId,
      );

      const nextUnitIndex = findIndex(
        [...units?.slice(currentUnitIndex + 1)],
        (unit: any) => !unit.isFarOffMoveout,
      );

      if (
        currentStep.gate === STEP_GATE_NAMES.BuildListing &&
        units?.length > 1 &&
        currentUnitIndex !== units?.length - 1 &&
        nextUnitIndex !== -1
      ) {
        // Go to the next unit
        const nextUnit = units?.[currentUnitIndex + 1];
        const nextUnitId = nextUnit.basicInfo.unitId;
        const newStepsByGate: Record<string, any[]> = getFilteredGateSteps({
          // `useSteps` returns `steps` and `allStepsByGate` together — both null
          // or both populated — and reaching here required a resolved
          // `currentStep`, which came out of `steps`.
          allStepsByGate: allStepsByGate!,
          unitId: nextUnitId,
          selectedProperty,
          user,
        });

        filteredSteps =
          newStepsByGate?.[STEP_GATE_NAMES.InspectionSchedule]?.filter(
            isStepUnHidden,
          );

        const [firstStep] = filteredSteps || [];

        if (!firstStep) {
          trackMixpanelEvent('flow-completed', {
            flowType: 'HomeownerSetup',
            homeId: selectedProperty?.basicInfo.propertyId,
          });
          history.push(`${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${propertyId}`);
        } else {
          history.push({
            pathname: getStepPath(firstStep, propertyId, nextUnitId),
            search: 'showNextUnitBanner=true',
          });
        }
      } else {
        const gates = Object.keys(STEP_GATE_NAMES);

        const currentGateIndex = findIndex(
          gates,
          (gateName: any) => gateName === currentStep.gate,
        );

        // Past the last gate there is no next gate name. Indexing `stepsByGate`
        // with `undefined` reads the literal `"undefined"` key today, so it
        // already resolves to no steps; this just says so.
        const nextGate = gates[currentGateIndex + 1];

        filteredSteps = nextGate
          ? stepsByGate?.[nextGate]?.filter(isStepUnHidden)
          : undefined;

        const [firstStep] = filteredSteps || [];

        if (!firstStep) {
          trackMixpanelEvent('flow-completed', {
            flowType: 'HomeownerSetup',
            homeId: selectedProperty?.basicInfo.propertyId,
          });
          history.push(`${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${propertyId}`);
        } else {
          history.push(getStepPath(firstStep, propertyId, unitId));
        }
      }
    }
  }

  function goPreviousStep() {
    const filteredSteps =
      stepsByGate?.[currentStep.gate]?.filter(isStepUnHidden);

    const currentStepIndex = findIndex(
      filteredSteps,
      (step: any) => step.uniqueId === currentStep.uniqueId,
    );
    const step = filteredSteps?.[currentStepIndex - 1];

    history.push(getStepPath(step, propertyId, unitId));
  }

  async function handleSaveAndNext(values: any) {
    setLoading(true);

    try {
      if (currentStep.flowType === FlowType.HomeownerSetup) {
        await dispatch(
          updateHomeownerSetUpStepData(currentStep.flowId, currentStep, values),
        );
      }
      if (currentStep.flowType === FlowType.UserPayment) {
        await dispatch(
          updateUserPaymentStepData(currentStep.flowId, currentStep, values),
        );
      }
      if (currentStep.flowType === FlowType.HomeInspection) {
        await dispatch(
          updateHomeInspectionStepData(currentStep.flowId, currentStep, values),
        );
      }
      if (currentStep.flowType === FlowType.HomeListing) {
        await dispatch(
          updateHomeListingStepData(currentStep.flowId, currentStep, values),
        );
      }

      await fetchFlows();

      setGoNext(true);
    } catch (error) {
      if (error && Array.isArray(error) && error.length > 0) {
        const errorMessage =
          error[0]?.message || 'An error occurred. Please try again.';
        dispatch(
          showToast(
            { message: errorMessage, error: true },
            { autoDelete: true, autoDeleteTime: 5000 },
          ),
        );
      } else {
        dispatch(
          showToast(
            { message: 'An error occurred. Please try again.', error: true },
            { autoDelete: true, autoDeleteTime: 5000 },
          ),
        );
      }

      setLoading(false);

      throw error;
    }
  }

  async function handleSave(values: any) {
    setLoading(true);
    try {
      if (currentStep.flowType === FlowType.HomeownerSetup) {
        await dispatch(
          updateHomeownerSetUpStepData(currentStep.flowId, currentStep, values),
        );
      }
      if (currentStep.flowType === FlowType.UserPayment) {
        await dispatch(
          updateUserPaymentStepData(currentStep.flowId, currentStep, values),
        );
      }
      if (currentStep.flowType === FlowType.UserPayment) {
        await dispatch(
          updateHomeownerSetUpStepData(currentStep.flowId, currentStep, values),
        );
      }
      if (currentStep.flowType === FlowType.HomeListing) {
        // NOTE: this is for the disclosures step
        await dispatch(
          updateHomeListingStepData(currentStep.flowId, currentStep, values),
        );
      }

      await fetchFlows();

      const fetchedData = await fetchData();

      return fetchedData;
    } catch (error) {
      if (error && Array.isArray(error) && error.length > 0) {
        const errorMessage =
          error[0]?.message || 'An error occurred. Please try again.';
        dispatch(
          showToast(
            { message: errorMessage, error: true },
            { autoDelete: true, autoDeleteTime: 5000 },
          ),
        );
      } else {
        dispatch(
          showToast(
            { message: 'An error occurred. Please try again.', error: true },
            { autoDelete: true, autoDeleteTime: 5000 },
          ),
        );
      }

      throw error;
    } finally {
      setLoading(false);
    }
  }

  function isValidUrl() {
    if (!flowName && !stepParam && !stepId) {
      return true;
    }

    if (flowName === 'success' && !stepParam && !stepId) {
      const allStepsCompleted = steps
        ?.filter(isStepUnHidden)
        ?.every(isStepCompleted);

      return !!allStepsCompleted;
    }

    const [stepBasedOnStepId] =
      steps?.filter((step) => step.uniqueId === stepId) ?? [];

    if (!stepBasedOnStepId) {
      return false;
    }

    const {
      group: { key: groupKey },
      key,
    } = stepBasedOnStepId;

    if (groupKey === flowName && key === stepParam) {
      return true;
    }

    return false;
  }

  /**
   * The tip for the current step. Some steps are shared by several markets — the "Licenses" step is
   * one flow step covering DC and MI — and the tip is keyed by step name alone, so those steps keep
   * per-state copy under `byState` and it wins when the step's payload names that state. Steps
   * without `byState`, and states without an entry, fall through to the step's own copy.
   */
  function getSidebarTip() {
    const stepName = currentStepState?.currentStep?.stepName;
    const stepGroupName = currentStepState?.currentStep?.group?.name;
    if (!stepName || !stepGroupName) return undefined;

    const tip = STEP_TIPS[stepGroupName]?.[stepName];
    if (!tip) return undefined;

    const state = currentStepState?.currentStepFormData?.state;

    return (state && tip.byState?.[state]) || tip;
  }

  function getSidebarTitleText() {
    const tip = getSidebarTip();

    return tip ? formatString(tip.title) : '';
  }

  function getSidebarDescriptions() {
    const tip = getSidebarTip();
    if (!tip) return [];

    if (selectedUnit?.isAdoptedAgreement || propertyWithAdoptedResidents) {
      return Object.keys(tip).includes('descriptions.adopted_resident')
        ? tip['descriptions.adopted_resident']
        : tip.descriptions;
    }

    return tip.descriptions;
  }

  function getSidebarDescriptionImages() {
    return getSidebarTip()?.descriptionImages || [];
  }

  function getSidebarImage() {
    return getSidebarTip()?.image;
  }

  useEffect(() => {
    async function fetch() {
      setLoading(true);

      await fetchData();

      setLoading(false);
    }

    fetch();
  }, [fetchData]);

  useEffect(() => {
    async function init() {
      setLoading(true);

      const newStepsConfig = getSetupFlowSteps(propertyId, unitId);

      setStepsConfig(newStepsConfig);
      setLoading(false);
    }

    init();
  }, [propertyId, unitId]);

  return {
    properties,
    steps,
    allStepsByGate,
    loading,
    stepsConfig,
    currentStepFormData,
    currentStep,
    propertyId,
    unitId,
    stepsByGate,
    selectedProperty,
    fetchProperties,
    setLoading,
    getSidebarDescriptions,
    getSidebarTitleText,
    getSidebarImage,
    isValidUrl,
    handleSaveAndNext,
    goPreviousStep,
    isLastStep,
    handleSave,
    goNextStep,
    getSidebarDescriptionImages,
    refetchCurrentStepData: fetchData,
  };
}
